import { VAT_SUMMARY_SOURCE_LABELS, type VatAccountRow, type VatSummary } from "@station/contracts";
import { Alert, Button, Card, DateInput, FormField, SkeletonRows } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, todayIstanbul } from "../../../features/accounting/shared.js";
import { ExpensesPage, Section } from "../../../features/expenses/shared.js";
import { useTaxCalendarCrumb, useVatSummary } from "../../../features/tax-calendar/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({ ...companySearchShape, from: date, to: date });

export const Route = createFileRoute("/_app/vergi-takvimi/kdv-ozeti")({
  validateSearch: (search) => searchSchema.parse(search),
  component: VatSummaryPage,
});

/** Dönem varsayılanı: içinde bulunulan ayın tamamı (Europe/Istanbul). */
function defaultRange(): { from: string; to: string } {
  const today = todayIstanbul();
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 1) - 86_400_000).getUTCDate();
  return {
    from: `${today.slice(0, 7)}-01`,
    to: `${today.slice(0, 7)}-${String(lastDay).padStart(2, "0")}`,
  };
}

function VatSummaryPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const crumb = useTaxCalendarCrumb();
  const fallback = defaultRange();
  const from = search.from ?? fallback.from;
  const to = search.to ?? fallback.to;

  return (
    <ExpensesPage
      title={t("taxCalendar.vatSummary.title")}
      description={t("taxCalendar.vatSummary.description")}
      breadcrumb={[crumb, { label: t("taxCalendar.vatSummary.title") }]}
    >
      {({ companyId }) => (
        <div className="grid gap-4">
          <Card className="flex flex-wrap items-end gap-3 p-4">
            <FormField label={t("taxCalendar.vatSummary.from")} className="w-44">
              {(control) => (
                <DateInput
                  {...control}
                  value={from}
                  onValueChange={(value) =>
                    void navigate({
                      search: (previous) => ({ ...previous, from: value ?? undefined }),
                      replace: true,
                    })
                  }
                />
              )}
            </FormField>
            <FormField label={t("taxCalendar.vatSummary.to")} className="w-44">
              {(control) => (
                <DateInput
                  {...control}
                  value={to}
                  onValueChange={(value) =>
                    void navigate({
                      search: (previous) => ({ ...previous, to: value ?? undefined }),
                      replace: true,
                    })
                  }
                />
              )}
            </FormField>
            <Button
              variant="outline"
              onClick={() =>
                void navigate({ search: (previous) => ({ ...previous, ...defaultRange() }), replace: true })
              }
            >
              {t("taxCalendar.vatSummary.thisMonth")}
            </Button>
          </Card>
          <SummaryBody companyId={companyId} from={from} to={to} />
        </div>
      )}
    </ExpensesPage>
  );
}

function SummaryBody({ companyId, from, to }: { companyId: string; from: string; to: string }): ReactElement {
  const summary = useVatSummary(companyId, from, to, from <= to);
  if (summary.error) return <Alert tone="danger">{errorMessage(summary.error)}</Alert>;
  if (!summary.data) return <SkeletonRows rows={8} />;
  return <SummaryContent summary={summary.data} />;
}

function SummaryContent({ summary }: { summary: VatSummary }): ReactElement {
  const { t } = useTranslation();
  const totalsId = useId();
  return (
    <div className="grid gap-4">
      {/* Uyarı metni SUNUCUDAN gelir ve belirgin gösterilir: bu rapor beyanname değildir. */}
      <Alert tone="warning" title={t("taxCalendar.vatSummary.disclaimerTitle")}>
        {summary.disclaimer}
      </Alert>

      <Card className="grid gap-2 p-4" aria-labelledby={totalsId}>
        <h2 id={totalsId} className="text-base font-semibold">
          {t("taxCalendar.vatSummary.totals")}
        </h2>
        <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          {(
            [
              [t("taxCalendar.vatSummary.totalOutput"), summary.totalOutput],
              [t("taxCalendar.vatSummary.totalInput"), summary.totalInput],
              [t("taxCalendar.vatSummary.payable"), summary.payable],
              [t("taxCalendar.vatSummary.carriedForward"), summary.carriedForward],
            ] as Array<[string, string]>
          ).map(([label, value]) => (
            <div key={label} className="grid gap-0.5">
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="text-lg font-semibold">
                <Amount value={value} />
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-muted-foreground">
          {t("taxCalendar.vatSummary.period", {
            from: formatDate(summary.from),
            to: formatDate(summary.to),
          })}
        </p>
      </Card>

      <AccountTable title={t("taxCalendar.vatSummary.outputAccounts")} rows={summary.outputAccounts} />
      <AccountTable title={t("taxCalendar.vatSummary.inputAccounts")} rows={summary.inputAccounts} />
      <AccountTable
        title={t("taxCalendar.vatSummary.taxesPayableAccounts")}
        rows={summary.taxesPayableAccounts}
      />

      <Section title={t("taxCalendar.vatSummary.documents")}>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">{t("taxCalendar.vatSummary.documents")}</caption>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("taxCalendar.vatSummary.source")}
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("taxCalendar.vatSummary.vatRateCode")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("taxCalendar.vatSummary.documentCount")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("taxCalendar.vatSummary.netAmount")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("taxCalendar.vatSummary.vatAmount")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("taxCalendar.vatSummary.withheldAmount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.documents.map((row, index) => (
                <tr key={`${row.source}-${row.vatRateCode}-${index}`} className="border-t">
                  <td className="px-3 py-2">{VAT_SUMMARY_SOURCE_LABELS[row.source]}</td>
                  <td className="px-3 py-2">
                    {row.vatRateCode}
                    {row.vatRate ? ` (%${row.vatRate})` : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular">{row.documentCount}</td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={row.netAmount} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={row.vatAmount} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={row.withheldAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function AccountTable({ title, rows }: { title: string; rows: VatAccountRow[] }): ReactElement | null {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <Section title={title}>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{title}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("accounting.fields.account")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("accounting.fields.debit")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("accounting.fields.credit")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("taxCalendar.vatSummary.net")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.accountCode} className="border-t">
                <td className="px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">{row.accountCode}</span>{" "}
                  {row.accountName}
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={row.debit} blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={row.credit} blankZero />
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  <Amount value={row.net} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
